import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import {
  PendingInvite, SharedWorkspace, SharedWorkspaceService,
} from '../services/shared-workspace.service';

/**
 * Shared workspaces: the ones you created for other people, the ones you were
 * invited into, and the invitations still waiting on you.
 *
 * Kept separate from /workspace, which lists the personal workspaces you work
 * in. A personal workspace cannot be shared and never appears here.
 */
@Component({
  selector: 'app-shared-workspaces',
  standalone: false,
  templateUrl: './shared-workspaces.component.html',
  styleUrls: ['./shared-workspaces.component.css'],
})
export class SharedWorkspacesComponent implements OnInit {

  owned: SharedWorkspace[] = [];
  sharedWithMe: SharedWorkspace[] = [];
  pending: PendingInvite[] = [];
  // Personal workspaces that could become shared. The default one is never
  // here: it holds everything written by default, so nothing in it was ever
  // meant for an audience.
  convertible: SharedWorkspace[] = [];

  loading = true;
  showCreate = false;
  newName = '';
  busy = false;

  constructor(
    private workspaces: SharedWorkspaceService,
    private toastr: ToastrService,
    public router: Router,
  ) { }

  ngOnInit(): void {
    this.load();
  }

  async load(): Promise<void> {
    this.loading = true;
    try {
      const [list, invites] = await Promise.all([
        this.workspaces.listWorkspaces(),
        this.workspaces.myInvites(),
      ]);
      // Personal workspaces live on the other page; this one is only about
      // workspaces that can have an audience.
      this.owned = (list?.owned || []).filter(w => w.shareable);
      this.convertible = (list?.owned || []).filter(w => !w.shareable && !w.default);
      this.sharedWithMe = list?.sharedWithMe || [];
      this.pending = invites || [];
    } catch (error: any) {
      this.toastr.error(error.message);
    } finally {
      this.loading = false;
    }
  }

  async create(): Promise<void> {
    const name = (this.newName || '').trim();
    if (!name) {
      this.toastr.error('Give the workspace a name.');
      return;
    }
    this.busy = true;
    try {
      const created = await this.workspaces.createWorkspace(name);
      this.toastr.success(`"${created.name}" is ready. Invite someone to it.`);
      this.newName = '';
      this.showCreate = false;
      await this.load();
      this.open(created.workspaceId);
    } catch (error: any) {
      this.toastr.error(error.message);
    } finally {
      this.busy = false;
    }
  }

  async makeShareable(workspace: SharedWorkspace): Promise<void> {
    this.busy = true;
    try {
      await this.workspaces.convertToShared(workspace.workspaceId);
      this.toastr.success(`"${workspace.name}" can now have members.`);
      await this.load();
      this.open(workspace.workspaceId);
    } catch (error: any) {
      if (!error.requiresConfirmation) {
        this.toastr.error(error.message);
        return;
      }
      // Say the quantity out loud. "Are you sure?" in front of an unknown
      // number of memories gets clicked through; a number does not.
      const confirmed = confirm(
        `${error.message}\n\n` +
        `Only invite people you want reading all of it. ` +
        `Making the workspace private again will not take back what they have seen.`);
      if (!confirmed) {
        return;
      }
      try {
        await this.workspaces.convertToShared(workspace.workspaceId, true);
        this.toastr.success(`"${workspace.name}" can now have members.`);
        await this.load();
        this.open(workspace.workspaceId);
      } catch (retryError: any) {
        this.toastr.error(retryError.message);
      }
    } finally {
      this.busy = false;
    }
  }

  async accept(invite: PendingInvite): Promise<void> {
    this.busy = true;
    try {
      await this.workspaces.acceptInvite({ inviteId: invite.inviteId });
      this.toastr.success(`You can now read ${invite.workspaceName}.`);
      await this.load();
    } catch (error: any) {
      this.toastr.error(error.message);
    } finally {
      this.busy = false;
    }
  }

  async decline(invite: PendingInvite): Promise<void> {
    this.busy = true;
    try {
      await this.workspaces.declineInvite({ inviteId: invite.inviteId });
      this.toastr.info(`Invitation to ${invite.workspaceName} declined.`);
      await this.load();
    } catch (error: any) {
      this.toastr.error(error.message);
    } finally {
      this.busy = false;
    }
  }

  async leave(workspace: SharedWorkspace): Promise<void> {
    if (!confirm(`Leave "${workspace.name}"? You will stop seeing what is shared there.`)) {
      return;
    }
    this.busy = true;
    try {
      await this.workspaces.removeMember(workspace.workspaceId);
      this.toastr.info(`You have left ${workspace.name}.`);
      await this.load();
    } catch (error: any) {
      this.toastr.error(error.message);
    } finally {
      this.busy = false;
    }
  }

  open(workspaceId: number): void {
    this.router.navigate(['/shared-workspace', workspaceId]);
  }

  formatDate(ts: number): string {
    if (!ts) {
      return '';
    }
    return new Date(ts * 1000).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  }

  expiresIn(ts: number): string {
    if (!ts) {
      return '';
    }
    const days = Math.ceil((ts * 1000 - Date.now()) / (24 * 60 * 60 * 1000));
    if (days <= 0) {
      return 'expires today';
    }
    return days === 1 ? 'expires tomorrow' : `expires in ${days} days`;
  }
}
